import styled from '@emotion/styled'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import React, { useMemo } from 'react'
import { COLORS } from '@borrowell/bw-styles'

import { FONT_FAMILY } from '../styles/theme'

import useWindowScroll from '../hooks/useWindowScroll'

import { Button } from '../components/Button'
import { Image } from '../components/Image'
import { LanguageToggle } from '../components/LanguageToggle'
import { Link } from '../components/Link'
import { NavDropdownMobile } from '../components/NavDropdownMobile'
import { NavLinkMobile } from '../components/NavLinkMobile'
import { HamburgerButton } from '../components/HamburgerButton'

import { ILink } from '../graphql/modules/link'
import { ILinksList } from '../graphql/modules/linksList'
import { IHeader } from '../graphql/sections/header'
import { useSendNavigationOpenedEvent } from '../hooks/analytics/useSendNavigationOpenedEvent'

interface IProps {
  data: {
    contentfulHeader: IHeader
    fixedLogo: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

const HeaderWrapper = styled.header<{ isScrolled: boolean }>`
  position: fixed;
  width: 100%;
  z-index: 100;
  background-color: ${COLORS.WHITE};
  display: block;

  @media (min-width: 900px) {
    display: none;
  }
`

const HeaderSpacer = styled.div`
  height: 80px;

  // hides the header save area on desktop which causes the back link to be hidden
  @media (min-width: 900px) {
    display: none;
  }

  @media (max-width: 900px) {
    height: 20px;
  }
`

const TopBar = styled.div<{ menuOpen: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px 40px;
  max-width: 1440px;
  margin: auto;
  z-index: 101;
  background-color: ${COLORS.WHITE};
  box-shadow: 0px 1px 5px 0px #0000000d;

  /* Mobile adjustments */
  @media (max-width: 599px) {
    padding: 20px 16px;
    box-shadow: ${({ menuOpen }) => (menuOpen ? 'none' : '0px 1px 5px 0px #0000000d')};
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`

const CompanyLogoWrapper = styled.div`
  height: 24px;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const LoginLink = styled.div`
  display: flex;
  align-items: center;

  a {
    font-family: ${FONT_FAMILY.LATO};
    font-size: 18px;
    font-weight: 800; // ExtraBold
    line-height: 24px;
    letter-spacing: 0.16px;
    color: #512fb8;
    text-decoration: none;
    transition: color 0.15s ease-in-out;

    &:hover {
      color: ${COLORS.PRIMARY['900']};
    }

    &:visited {
      color: #512fb8;
    }

    &:visited:hover {
      color: ${COLORS.PRIMARY['900']};
    }
  }
`

const MobileMenu = styled.nav<{ isOpen: boolean }>`
  position: fixed;
  background-color: ${COLORS.WHITE};
  overflow-y: auto;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 50;
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  /* Mobile version: 0-599px - Full screen, slide down from top */
  @media (max-width: 599px) {
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  }

  /* Tablet version: 600px-1279px - Side panel, slide in from right */
  @media (min-width: 600px) {
    top: 0;
    right: 0;
    width: 100%;
    max-width: 375px;
    height: 100vh;
    box-shadow: -2px 0 5px 0px #0000000d;
    transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  }
`

const MenuContent = styled.div`
  margin-top: 94px;

  /* Mobile: use flex to push buttons to bottom */
  @media (max-width: 599px) {
    display: flex;
    flex-direction: column;
    min-height: calc(100dvh - 94px);
  }

  /* Tablet: 16px left padding, 0 right padding */
  @media (min-width: 600px) {
    padding: 0 0 0 16px;
  }
`

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;

  /* Mobile: sit directly below the nav links and add padding */
  @media (max-width: 599px) {
    margin-top: 0;
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 24px;
  }

  /* Tablet: border full width, content has margin both sides */
  @media (min-width: 600px) {
    border-top: 1px solid ${COLORS.NEUTRAL.COOL['100']};
    margin-left: 16px;
    margin-right: 16px;
  }
`

const companyLogoStyles = {
  width: '106px',
  height: '24px',
}

const customLoginButtonStyles = {
  width: '100%',
  height: '44px',
  borderRadius: '22px',
  border: `solid 2px ${COLORS.PURPLE['700']}`,
  backgroundColor: 'white',
  fontSize: '16px',
  letterSpacing: '0.2px',
  fontWeight: 'bold',
  lineHeight: 'normal',
  margin: '0px',
  ':hover': {
    backgroundColor: COLORS.PURPLE['050'],
  },
}

const customSignupButtonStyles = {
  width: '100%',
  height: '44px',
  borderRadius: '22px',
  fontSize: '16px',
  letterSpacing: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.86',
  margin: '0px',
}

export const HeaderMobile: React.FC<IProps> = React.memo(
  ({
    data: {
      contentfulHeader: { login, signup, logoLink, navbar, company },
      fixedLogo: { gatsbyImageData: companyLogoImage },
    },
  }) => {
    const [menuOpen, setMenuOpen] = React.useState(false)
    const [isScrolled] = useWindowScroll()
    const [openMobileDropdown, setOpenMobileDropdown] = React.useState<string | null>(null)
    const sendNavigationOpenedEvent = useSendNavigationOpenedEvent()

    React.useEffect(() => {
      document.body.style.overflowY = menuOpen ? 'hidden' : 'scroll'
      return () => {
        document.body.style.overflowY = 'scroll'
      }
    }, [menuOpen])

    const {
      link: companyLogoLink,
      rel: companyLogoLinkRel,
      contentful_id: companyLogoContentfulId,
      linkText: companyLogoLinkText,
      __typename: companyLogoTypeName,
    } = { ...logoLink, linkText: 'Borrowell Logo' }

    const {
      link: loginButtonLink,
      linkName: loginButtonName,
      linkText: loginButtonText,
      rel: loginButtonRel,
      contentful_id: loginButtonContentfulId,
      __typename: loginButtonTypeName,
    } = login

    const {
      link: signupButtonLink,
      linkName: signupButtonName,
      linkText: signupButtonText,
      rel: signupButtonRel,
      contentful_id: signupButtonContentfulId,
      __typename: signupButtonTypeName,
    } = signup

    const { alternateText: companyLogoAltText } = company.logo.alternateText

    const handleToggle = () => {
      /**
       * If the menu is about to be opened, send a navigation opened event
       */
      if (!menuOpen) {
        sendNavigationOpenedEvent({
          entry_id: 'hamburger-menu',
          contentful_type: 'hamburger-menu',
          link_text: 'Hamburger Menu',
          header_version: 'v2',
          position: 'Hamburger Menu',
        })
      }
      setMenuOpen(!menuOpen)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleToggle()
      }
    }

    const closeMenu = () => {
      setMenuOpen(false)
      setOpenMobileDropdown(null)
    }

    // Memoized Company Logo
    const companyLogoSection = useMemo(
      () => (
        <CompanyLogoWrapper>
          <Link
            name="company-logo"
            to={companyLogoLink}
            text={companyLogoLinkText}
            rel={companyLogoLinkRel}
            ariaLabel={companyLogoAltText}
            contentfulId={companyLogoContentfulId}
            typeName={companyLogoTypeName}
            withoutTextStyling
            onClick={closeMenu}
          >
            <Image fluid={companyLogoImage} alt={companyLogoAltText} customStyles={companyLogoStyles} />
          </Link>
        </CompanyLogoWrapper>
      ),
      [
        companyLogoLink,
        companyLogoLinkRel,
        companyLogoAltText,
        companyLogoContentfulId,
        companyLogoTypeName,
        companyLogoImage,
        companyLogoLinkText,
      ],
    )

    return (
      <>
        <HeaderWrapper isScrolled={isScrolled} data-header-version="v2" data-header-position="Hamburger Menu">
          <TopBar menuOpen={menuOpen}>
            <LeftSection>{companyLogoSection}</LeftSection>

            <RightSection>
              <LanguageToggle />
              <LoginLink>
                <Link
                  to={loginButtonLink}
                  rel={loginButtonRel}
                  name={loginButtonName}
                  text={loginButtonText}
                  withoutTextStyling
                  contentfulId={loginButtonContentfulId}
                  typeName={loginButtonTypeName}
                />
              </LoginLink>
              <HamburgerButton isOpen={menuOpen} onClick={handleToggle} onKeyDown={handleKeyDown} />
            </RightSection>
          </TopBar>

          <MobileMenu isOpen={menuOpen}>
            <MenuContent>
              <NavList>
                {navbar.map((navItem, index) => {
                  // Check if this is a LinksList (dropdown) or a regular Link
                  if (navItem.__typename === 'ContentfulLinksList') {
                    const linksList = navItem as ILinksList
                    return (
                      <NavDropdownMobile
                        key={linksList.contentful_id || index}
                        parentItem={{
                          linkText: linksList.title,
                          contentful_id: linksList.contentful_id,
                          __typename: linksList.__typename,
                        }}
                        childItems={linksList.links.map(child => ({
                          linkText: child.linkText,
                          link: child.link,
                          rel: child.rel,
                          contentful_id: child.contentful_id,
                          __typename: child.__typename,
                        }))}
                        isOpen={openMobileDropdown === linksList.contentful_id}
                        onToggle={() => {
                          setOpenMobileDropdown(
                            openMobileDropdown === linksList.contentful_id ? null : linksList.contentful_id,
                          )
                        }}
                        onChildClick={closeMenu}
                      />
                    )
                  } else {
                    // Render regular link
                    const link = navItem as ILink
                    return (
                      <NavLinkMobile
                        key={link.contentful_id || index}
                        linkText={link.linkText}
                        link={link.link}
                        rel={link.rel}
                        contentful_id={link.contentful_id}
                        __typename={link.__typename}
                        onClick={closeMenu}
                      />
                    )
                  }
                })}
              </NavList>

              <ButtonsWrapper>
                <Button
                  to={loginButtonLink}
                  rel={loginButtonRel}
                  name={loginButtonName}
                  styleOverrides={customLoginButtonStyles}
                  contentfulId={loginButtonContentfulId}
                  typeName={loginButtonTypeName}
                >
                  {loginButtonText}
                </Button>
                <Button
                  to={signupButtonLink}
                  rel={signupButtonRel}
                  name={signupButtonName}
                  styleOverrides={customSignupButtonStyles}
                  contentfulId={signupButtonContentfulId}
                  typeName={signupButtonTypeName}
                >
                  {signupButtonText}
                </Button>
              </ButtonsWrapper>
            </MenuContent>
          </MobileMenu>
        </HeaderWrapper>
        <HeaderSpacer />
      </>
    )
  },
)

HeaderMobile.displayName = 'HeaderMobile'
